<template>
    <div>
        <div v-if="mode === 'edit'" class="flex-row">
            <el-input
                :ref="reference"
                v-model="localValue.decimalValue"
                :placeholder="
                    $filters.numberFormatWithOptions(prop.defaultValue, amountNumberFormat, false)
                "
                :min="parseInt(prop.min)"
                :max="parseInt(prop.max)"
                :controls="false"
                @blur="handleBlur"
            >
            </el-input>
            <el-select v-model="localValue.numericValue" class="ml-2">
                <el-option
                    v-for="(allowedValue, index) in formattedCurrencies"
                    :key="index"
                    :label="allowedValue.name"
                    :value="parseInt(allowedValue.id)"
                >
                </el-option>
            </el-select>
        </div>
        <p v-else>
            {{ $filters.numberFormatWithOptions(value.decimalValue, amountNumberFormat, false) }}
            {{ currency }}
        </p>
    </div>
</template>

<script>
import CfMixin from '../../../mixins/CustomfieldInputsMixins';
import { deepEqual } from 'Helpers/deepEqual';

export default {
    mixins: [CfMixin],
    emits: ['update:modelValue'],
    data() {
        return {
            localValue: null,
            amountNumberFormat: { ...window.App.numformat },
        };
    },
    computed: {
        /**
         * Returns label of currency selected
         */
        currency() {
            return this.prop.allowedValues.find(
                (value) => parseInt(value.id) == this.localValue.numericValue,
            )?.name;
        },
        /**
         * Returns the float value of decimalValue
         */
        decimalValueFloat() {
            return (decimalValue) =>
                NumberFormat.formatToFloat(decimalValue, this.amountNumberFormat);
        },
        formattedCurrencies() {
            let currencies = [];
            if (this.localValue.numericValue === 0) {
                currencies = [
                    { id: 0, name: this.$t.__('common.labels.customFields.free_currency') },
                ];
            }
            return currencies.concat(this.prop.allowedValues);
        },
    },
    watch: {
        localValue: {
            handler: function (newValue, oldValue) {
                if (!deepEqual(newValue, oldValue)) {
                    if (this.checkParams(newValue)) {
                        this.$emit('update:modelValue', this.adaptToOutput(newValue));
                    }
                }
            },
            deep: true,
        },
        value: {
            handler: function (newValue, oldValue) {
                if (!deepEqual(newValue, oldValue)) {
                    this.localValue = newValue;
                }
            },
            deep: true,
        },
    },

    created() {
        this.localValue = this.adaptToInput(this.value);
    },

    methods: {
        /**
         * Check if decimalValue is a correct value
         */
        checkParams({ decimalValue }) {
            // we accept an empty field
            if (!decimalValue) return true;

            // we accept a decimalValue egal to 0
            if (parseFloat(decimalValue) === 0) return true;

            // we format the entered value to be correctly interpretable for the check
            // valueToCheck will be eglal to 0 if isNaN
            const valueToCheck = this.decimalValueFloat(decimalValue);

            // we do not accept any value other than a number
            if (valueToCheck === 0) return false;

            // we check the min max values
            return this.checkMinMaxParams(parseFloat(valueToCheck));
        },
        /**
         * On blur, format the input value correctly
         */
        handleBlur(e) {
            if (e.target.value)
                this.localValue.decimalValue = NumberFormat.formatToDisplay(
                    this.decimalValueFloat(e.target.value),
                    this.amountNumberFormat,
                    false,
                );
        },
        /**
         * Adapt the "value" to a format compatible with "localValue"
         * Back provided a decimal value with a floating number format (e.g. "1342183.23")
         */
        adaptToInput(value) {
            return {
                ...this.templateData,
                ...value,
                decimalValue:
                    this.prop.defaultValue || value
                        ? NumberFormat.formatToDisplay(
                              value?.decimalValue ?? this.prop.defaultValue,
                              this.amountNumberFormat,
                              false,
                          )
                        : '',
                ...(!value && {
                    numericValue: parseInt(
                        (
                            this.prop.allowedValues.find((allowed) => allowed.default) ||
                            this.prop.allowedValues[0]
                        ).id,
                    ),
                }),
            };
        },
        /**
         * Adapt the "localValue" to a format compatible with "value"
         * Back wants a decimal value as a string and formatted with decimal and thousands separators (e.g. "1,342,183.23")
         */
        adaptToOutput(localValue) {
            return {
                ...localValue,
                decimalValue: !!localValue.decimalValue
                    ? NumberFormat.formatToDisplay(
                          this.decimalValueFloat(localValue.decimalValue),
                          this.amountNumberFormat,
                          false,
                      )
                    : '',
            };
        },
    },
};
</script>
